import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { StarRating } from '@/components/ui/star-rating'

interface Manual {
  id: number
  name: string
  description: string
  domain: string
  platforms: string[]
  tags: string[]
  keywords: string[]
  usage_count: number
  average_rating: number
  reviews_count: number
  needs_attention: boolean
  active: boolean
  rejection_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  message_id: number | null
  dev_instance_id: number | null
  created_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  manuals: Manual[]
  pagy: Pagination
  filters: {
    active: string
    name_or_description_cont?: string
    s?: string
    domain_in?: string[]
    platforms_overlap?: string[]
  }
  valid_domains: string[]
  valid_platforms: string[]
  stats: { active: number; rejected: number; total: number; needs_attention: number }
}

function Index({ manuals, pagy, filters, stats, valid_domains, valid_platforms }: Props) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState(filters.name_or_description_cont || '')
  const [selectedDomains, setSelectedDomains] = useState<string[]>(filters.domain_in || [])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(filters.platforms_overlap || [])

  const basePath = '/admin/collective_knowledge/manuals'

  const getStatusBadge = (active: boolean) => (
    <Badge variant={active ? 'success' : 'destructive'}>
      {active ? t('collectiveKnowledge.manuals.status.active') : t('collectiveKnowledge.manuals.status.rejected')}
    </Badge>
  )

  const buildParams = (overrides: Record<string, string | string[] | null> = {}) => {
    const params: Record<string, string | string[]> = { active: filters.active || 'true' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    if (selectedDomains.length > 0) params['q[domain_in][]'] = selectedDomains
    if (selectedPlatforms.length > 0) params['q[platforms_overlap][]'] = selectedPlatforms

    // Preserve current sort
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleStatusChange = (value: string) => {
    router.get(basePath, { ...buildParams(), active: value }, { preserveState: true })
  }

  const handleSearch = () => {
    router.get(basePath, buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearchQuery('')
    setSelectedDomains([])
    setSelectedPlatforms([])
    router.get(basePath, {}, { preserveState: true })
  }

  const handleDomainChange = (domains: string[]) => {
    setSelectedDomains(domains)
    const params: Record<string, string | string[]> = { active: filters.active || 'true' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    if (domains.length > 0) params['q[domain_in][]'] = domains
    if (selectedPlatforms.length > 0) params['q[platforms_overlap][]'] = selectedPlatforms
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort
    router.get(basePath, params, { preserveState: true })
  }

  const handlePlatformChange = (platforms: string[]) => {
    setSelectedPlatforms(platforms)
    const params: Record<string, string | string[]> = { active: filters.active || 'true' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    if (selectedDomains.length > 0) params['q[domain_in][]'] = selectedDomains
    if (platforms.length > 0) params['q[platforms_overlap][]'] = platforms
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort
    router.get(basePath, params, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')

    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    router.get(basePath, { ...buildParams(), 'q[s]': newSort }, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')

    if (currentSort === `${field} asc`) return '↑'
    if (currentSort === `${field} desc`) return '↓'
    return '⇅'
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('collectiveKnowledge.manuals.title')} ({pagy.count})
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Badge variant="success" className="text-[10px] px-1 py-0">{stats.active}</Badge>
                <span>/</span>
                <Badge variant="destructive" className="text-[10px] px-1 py-0">{stats.rejected}</Badge>
                {stats.needs_attention > 0 && (
                  <>
                    <span>/</span>
                    <Badge variant="warning" className="text-[10px] px-1 py-0">{stats.needs_attention} {t('collectiveKnowledge.manuals.stats.needsAttention')}</Badge>
                  </>
                )}
              </div>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.active || 'true'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder={t('collectiveKnowledge.manuals.filters.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('collectiveKnowledge.manuals.filters.all')}</SelectItem>
                  <SelectItem value="true">{t('collectiveKnowledge.manuals.status.active')}</SelectItem>
                  <SelectItem value="false">{t('collectiveKnowledge.manuals.status.rejected')}</SelectItem>
                </SelectContent>
              </Select>
              <MultiSelectFilter
                label={t('collectiveKnowledge.manuals.filters.domain')}
                options={valid_domains}
                selected={selectedDomains}
                onChange={handleDomainChange}
              />
              <MultiSelectFilter
                label={t('collectiveKnowledge.manuals.filters.platforms')}
                options={valid_platforms}
                selected={selectedPlatforms}
                onChange={handlePlatformChange}
              />
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder={t('collectiveKnowledge.manuals.filters.searchPlaceholder')}
                className="h-7 text-xs w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                {t('collectiveKnowledge.manuals.buttons.search')}
              </Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                {t('collectiveKnowledge.manuals.buttons.reset')}
              </Button>
            </div>

            {/* Manuals Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.manuals.table.name')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.manuals.table.domain')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.manuals.table.platforms')}
                      </th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('usage_count')}
                      >
                        <div className="flex items-center gap-1">
                          {t('collectiveKnowledge.manuals.table.usage')}
                          <span className="text-gray-400">{getSortIcon('usage_count')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.manuals.table.status')}
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('average_rating')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {t('collectiveKnowledge.manuals.table.rating')}
                          <span className="text-gray-400">{getSortIcon('average_rating')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          {t('collectiveKnowledge.manuals.table.created')}
                          <span className="text-gray-400">{getSortIcon('created_at')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.manuals.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {manuals.length > 0 ? (
                      manuals.map((manual) => (
                        <tr key={manual.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">{manual.name}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-md">{manual.description}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant="secondary" className="text-xs">{manual.domain}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <div className="flex gap-1">
                              {manual.platforms.map((p) => (
                                <Badge key={p} variant="secondary" className="text-[10px] px-1 py-0">{p}</Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-muted-foreground">{manual.usage_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {getStatusBadge(manual.active)}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            {manual.reviews_count > 0 ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <StarRating value={manual.average_rating} />
                                <span className="text-xs text-muted-foreground">({manual.reviews_count})</span>
                                {manual.needs_attention && (
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" title={t('collectiveKnowledge.manuals.stats.needsAttention')} />
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(manual.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`${basePath}/${manual.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              {t('collectiveKnowledge.manuals.buttons.view')}
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('collectiveKnowledge.manuals.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.manuals.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.manuals.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
